import { useState, useEffect } from "react";
import { Button, Form, Select, message, Input } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  InputTypes,
  MIN_VALUE,
  PERCENT_MAX_VALUE,
  AlertMessages,
  RESTRICTED_SYMBOLS,
} from "../../../../constants/Constants";

export const TablesGenerator = ({
  tableHeader,
  rowInputs,
  note,
  handleInputChange,
  questionId,
  pillarId,
  ReportForm,
  type = "TABLE",
}) => {
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [dependentOptions, setDependentOptions] = useState({});

  message.config({
    maxCount: 1,
  });

  useEffect(() => {
    initializeRows();
  }, [ReportForm]);

  const initializeRows = () => {
    const currentInputFields =
      ReportForm?.[pillarId]?.[questionId]?.inputFields || {};

    if (Object.keys(currentInputFields).length === 0) {
      setRows([{ id: "row001", isDefault: true }]);
      form.resetFields();
    } else {
      const sortedRowIds = Object.keys(currentInputFields).sort();
      const existingRows = sortedRowIds.map((rowId) => ({
        id: rowId,
        isDefault: rowId === "row001",
      }));
      setRows(existingRows);

      form.resetFields();
      form.setFieldsValue(currentInputFields);

      // Initialize dependent options for existing data
      existingRows.forEach((row) => {
        const rowData = currentInputFields[row.id];
        if (rowData) {
          updateDependentOptions(row.id, rowData);
        }
      });
    }
  };

  const updateDependentOptions = (rowId, currentValues) => {
    const newDependentOptions = { ...dependentOptions };

    rowInputs.forEach((input) => {
      if (input.dependsOn) {
        const parentValue = currentValues[input.dependsOn];
        if (parentValue && input.options[parentValue]) {
          if (!newDependentOptions[rowId]) {
            newDependentOptions[rowId] = {};
          }
          newDependentOptions[rowId][input.inputId] =
            input.options[parentValue];
        }
      }
    });

    setDependentOptions(newDependentOptions);
  };

  const calculateTotalEmissions = (
    rowId,
    updatedFieldId = null,
    updatedValue = null
  ) => {
    let total = 0;
    const currentRowValues = {
      ...(ReportForm?.[pillarId]?.[questionId]?.inputFields?.[rowId] || {}),
      ...(updatedFieldId ? { [updatedFieldId]: updatedValue } : {}),
    };

    rowInputs.forEach((input) => {
      if (
        input.inputType === InputTypes.NUMBER &&
        input.tableHeader !== "Total Emissions"
      ) {
        const value = parseFloat(currentRowValues[input.inputId]) || 0;
        total += value;
      }
    });

    return total.toFixed(2);
  };

  const generateRowId = (index) => {
    return `row${(index + 1).toString().padStart(3, "0")}`;
  };

  const isRowComplete = (rowId) => {
    const currentRowValues =
      ReportForm?.[pillarId]?.[questionId]?.inputFields?.[rowId] || {};

    return rowInputs.every((input) => {
      if (input.tableHeader === "Total Emissions" || !input.mandatory)
        return true;
      const value = currentRowValues[input.inputId];
      return value !== undefined && value !== "" && value !== null;
    });
  };

  
  const isRowDuplicate = (rowId, updatedFieldId = null, updatedValue = null) => {
    const currentRowValues = { 
      ...ReportForm?.[pillarId]?.[questionId]?.inputFields?.[rowId] || {},
      ...(updatedFieldId ? { [updatedFieldId]: updatedValue } : {})
    };
    

    const mandatoryFields = rowInputs
      .filter(input => input.mandatory && input.tableHeader !== "Total Emissions")
      .map(input => input.inputId);
    
    
    const hasAllRequiredValues = mandatoryFields.every(fieldId => 
      currentRowValues[fieldId] !== undefined && 
      currentRowValues[fieldId] !== null && 
      currentRowValues[fieldId] !== ''
    );
    
    if (!hasAllRequiredValues) return false;
    
    
    const otherRowIds = rows
      .filter(row => row.id !== rowId)
      .map(row => row.id);
    
   
    return otherRowIds.some(otherId => {
      const otherRowValues = ReportForm?.[pillarId]?.[questionId]?.inputFields?.[otherId] || {};
      
      
      const otherRowHasAllFields = mandatoryFields.every(fieldId => 
        otherRowValues[fieldId] !== undefined && 
        otherRowValues[fieldId] !== null && 
        otherRowValues[fieldId] !== ''
      );
      
      if (!otherRowHasAllFields) return false;
      
     
      return mandatoryFields.every(fieldId => {
        const currentValue = currentRowValues[fieldId];
        const otherValue = otherRowValues[fieldId];
        return currentValue === otherValue;
      });
    });
  };

  const addRow = () => {
    const lastRowId = rows[rows.length - 1].id;

    if (!isRowComplete(lastRowId)) {
      message.warning(AlertMessages.MISSING_FIELDS);
      return;
    }

    const newRowId = generateRowId(rows.length);
    rowInputs.forEach((input) => {
      if (input.tableHeader !== "Total Emissions") {
        handleInputChange(
          pillarId,
          questionId,
          input.inputId,
          input.inputType === InputTypes.NUMBER ? "" : "",
          null,
          newRowId,
          type
        );
      } else {
        handleInputChange(
          pillarId,
          questionId,
          input.inputId,
          "0.00",
          null,
          newRowId,
          type
        );
      }
    });

    setRows((prev) => [...prev, { id: newRowId, isDefault: false }]);
  };

  const deleteRow = (rowId) => {
    if (rows.length === 1) {
      form.resetFields();
      handleInputChange(pillarId, questionId, null, null, null, rowId, type);
      setRows([{ id: "row001", isDefault: true }]);

      // Clear dependent options for this row
      const newDependentOptions = { ...dependentOptions };
      delete newDependentOptions[rowId];
      setDependentOptions(newDependentOptions);
      return;
    }

    handleInputChange(pillarId, questionId, null, null, null, rowId, type);

    const updatedRows = rows.filter((row) => row.id !== rowId);
    const reorderedRows = updatedRows.map((row, index) => {
      const newId = generateRowId(index);
      if (row.id !== newId) {
        const oldRowData =
          ReportForm?.[pillarId]?.[questionId]?.inputFields?.[row.id];
        if (oldRowData) {
          Object.entries(oldRowData).forEach(([fieldId, value]) => {
            handleInputChange(
              pillarId,
              questionId,
              fieldId,
              value,
              null,
              newId,
              type
            );
          });
          handleInputChange(
            pillarId,
            questionId,
            null,
            null,
            null,
            row.id,
            type
          );
        }
      }
      return { ...row, id: newId };
    });

    // Update dependent options for reordered rows
    const newDependentOptions = {};
    reorderedRows.forEach((row) => {
      if (dependentOptions[row.id]) {
        newDependentOptions[row.id] = dependentOptions[row.id];
      }
    });
    setDependentOptions(newDependentOptions);

    setRows(reorderedRows);
  };

  const handleKeyDown = (e) => {
    if (RESTRICTED_SYMBOLS.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleFieldChange = (rowId, inputId, value) => {
    // Check for duplicates before updating
    if (isRowDuplicate(rowId, inputId, value)) {
      message.warning("Duplicate entries are not allowed. Please enter unique values for this row.");
      return;
    }
    
    const totalEmissionsField = rowInputs.find(input => input.tableHeader === "Total Emissions");
    
    // Handle dependent dropdowns
    const input = rowInputs.find((input) => input.inputId === inputId);
    if (input && input.options && typeof input.options === "object") {
      // This is a parent dropdown that affects other dropdowns
      const currentValues = {
        ...ReportForm?.[pillarId]?.[questionId]?.inputFields?.[rowId],
        [inputId]: value,
      };

      // Clear dependent fields
      const dependentFields = rowInputs.filter(
        (field) => field.dependsOn === inputId
      );
      dependentFields.forEach((field) => {
        handleInputChange(
          pillarId,
          questionId,
          field.inputId,
          "",
          null,
          rowId,
          type
        );

        // Also clear fields that depend on this field
        const secondaryDependentFields = rowInputs.filter(
          (f) => f.dependsOn === field.inputId
        );
        secondaryDependentFields.forEach((secField) => {
          handleInputChange(
            pillarId,
            questionId,
            secField.inputId,
            "",
            null,
            rowId,
            type
          );
        });
      });

      updateDependentOptions(rowId, currentValues);
    }

    handleInputChange(pillarId, questionId, inputId, value, null, rowId, type);

    if (totalEmissionsField) {
      const newTotal = calculateTotalEmissions(rowId, inputId, value);
      setTimeout(() => {
        handleInputChange(
          pillarId,
          questionId,
          totalEmissionsField.inputId,
          newTotal,
          null,
          rowId,
          type
        );
      }, 0);
    }
  };

  const getDropdownOptions = (inputField, rowId) => {
    if (inputField.dependsOn) {
      return dependentOptions[rowId]?.[inputField.inputId] || [];
    }
    return Array.isArray(inputField.options) ? inputField.options : [];
  };

  return (
    <div>
      {note && <div className="w-auto font-bold pb-[20px]">{note}</div>}
      <div className="overflow-x-auto rounded-[20px]">
        <div className="min-w-max p-4">
          <table className="bg-[#EAF5FF] rounded-[20px]  border-collapse border-none w-full">
            <thead>
              <tr>
                {tableHeader.map((header) => (
                  <th
                    key={header}
                    className="border-none p-2 text-[#1F405A] text-center whitespace-nowrap"
                  >
                    {header}
                    <span className="text-red-500 font-bold ml-0.5">*</span>
                  </th>
                ))}
                <th className="border-none p-2 text-[#1F405A] text-center whitespace-nowrap">
                  {AlertMessages.ACTIONS}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  {rowInputs.map((inputField) => {
                    const {
                      inputId,
                      inputType,
                      unit,
                      tableHeader: fieldHeader,
                    } = inputField;
                    const fieldName = `${inputId}-${row.id}`;
                    const isTotal = fieldHeader === "Total Emissions";
                    const fieldValue = isTotal
                      ? calculateTotalEmissions(row.id)
                      : ReportForm?.[pillarId]?.[questionId]?.inputFields?.[
                          row.id
                        ]?.[inputId] || "";
                    const options = getDropdownOptions(inputField, row.id);
                    const isDisabled =
                      isTotal ||
                      (inputField.dependsOn &&
                        !dependentOptions[row.id]?.[inputField.inputId]);

                    return (
                      <td key={fieldName} className="border-none p-2">
                        {inputType === InputTypes.DROPDOWN ? (
                          <Select
                            className="rounded-[10px] min-w-[120px] w-full text-center bg-white"
                            value={fieldValue}
                            onChange={(value) =>
                              handleFieldChange(row.id, inputId, value)
                            }
                            showSearch
                            disabled={isDisabled}
                          >
                            <Select.Option
                              value=""
                              disabled
                              hidden
                            ></Select.Option>
                            {options?.map((option) => (
                              <Select.Option key={option} value={option}>
                                {option}
                              </Select.Option>
                            ))}
                          </Select>
                        ) : (
                          <Input
                            type={
                              inputType === InputTypes.NUMBER
                                ? "number"
                                : "text"
                            }
                            value={fieldValue}
                            className={`rounded-[10px] pl-[15px] outline-none border border-gray-300 ${
                              inputType === InputTypes.NUMBER
                                ? "w-[100px]"
                                : "w-[150px]"
                            } ${isTotal ? "font-medium " : ""}`}
                            style={{
                              background: isTotal ? "white" : undefined,
                              WebkitTextFillColor: isTotal
                                ? "black"
                                : undefined,
                            }}
                            suffix={
                              inputType === InputTypes.NUMBER ? (
                                <span className="text-gray-400">{unit}</span>
                              ) : (
                                ""
                              )
                            }
                            onKeyDown={
                              inputType === InputTypes.NUMBER
                                ? handleKeyDown
                                : undefined
                            }
                            disabled={isTotal}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (inputType === InputTypes.NUMBER) {
                                const numericValue = Number(value);
                                if (numericValue > PERCENT_MAX_VALUE) {
                                  message.warning(
                                    "Value should be less than 100."
                                  );
                                  return;
                                }
                                if (numericValue < MIN_VALUE && value !== "") {
                                  message.warning(
                                    "Value should be greater than 0"
                                  );
                                  return;
                                }
                              }
                              if (
                                inputType !== InputTypes.NUMBER &&
                                value.length > 500
                              ) {
                                message.warning(
                                  "Text input cannot exceed 500 characters"
                                );
                                return;
                              }
                              handleFieldChange(row.id, inputId, value);
                            }}
                          />
                        )}
                      </td>
                    );
                  })}
                  <td className="border-none p-2 text-center">
                    <div className="flex gap-2  justify-center">
                      {row.id === rows[rows.length - 1].id && (
                        <Button
                          type="text"
                          icon={<PlusOutlined />}
                          onClick={() => addRow()}
                        />
                      )}
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => deleteRow(row.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TablesGenerator;
